import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ModelReviewInterface } from 'types/Apis/SearchURL';

import { colors } from 'components/styles/variables';
import ReviewerInfo from '../Reviews/ReviewerInfo';
import LabeledStarRatings from '../Reviews/LabeledStarRatings';
import { ReviewTitleContent, ReviewBodyContent } from '../Reviews/ReviewTextContent';
import StarRating from '../Reviews/StarRating';

export interface OwnerReviewProps {
  review: ModelReviewInterface;
}

export default function OwnerReview({ review }: OwnerReviewProps): JSX.Element {
  const { t } = useTranslation();
  const [isViewingMore, setIsViewingMore] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const onViewLess = (): void => {
    setIsViewingMore(false);
    scrollRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className={`owner-review ${isViewingMore && 'expanded-review'}`} ref={scrollRef}>
      <ReviewerInfo
        reviewerName={review.reviewerName}
        bestLocationDisplayName={review.bestLocationDisplayName}
      />
      <StarRating value={review.overallRating} />

      <ReviewTitleContent className="owner-review-title">
        {`${review.modelYear || ''} ${review.makeName} ${review.modelName} ${
          review.modelYear && review.makeName && review.modelName ? '-' : ''
        } ${review.title}`}
      </ReviewTitleContent>
      <ReviewBodyContent>{review.body}</ReviewBodyContent>

      {!isViewingMore && (
        <button
          onClick={(): void => setIsViewingMore(true)}
          className="owner-review-toggler-btn"
          data-testid="owner-review-view-more-btn"
        >
          {t('seoSrp.ownerReviews.viewMore')}
        </button>
      )}

      {isViewingMore && (
        <div className="owner-review-more-content" data-testid="owner-review-more-content">
          <ReviewTitleContent className="section-title">
            {t('seoSrp.ownerReviews.story')}
          </ReviewTitleContent>
          <ReviewBodyContent>{review.story}</ReviewBodyContent>

          <ReviewTitleContent className="section-title">
            {t('seoSrp.ownerReviews.pros')}
          </ReviewTitleContent>
          <ReviewBodyContent>{review.pros}</ReviewBodyContent>

          <ReviewTitleContent className="section-title">
            {t('seoSrp.ownerReviews.cons')}
          </ReviewTitleContent>
          <ReviewBodyContent>{review.cons}</ReviewBodyContent>

          <ReviewTitleContent className="section-title score-title">
            {t('seoSrp.ownerReviews.score')}
          </ReviewTitleContent>
          <div className="ratings">
            <LabeledStarRatings
              ratings={{
                [t('seoSrp.ownerReviews.value')]: review.valueRating,
                [t('seoSrp.ownerReviews.style')]: review.styleRating,
                [t('seoSrp.ownerReviews.performance')]: review.performanceRating,
                [t('seoSrp.ownerReviews.comfort')]: review.comfortRating,
                [t('seoSrp.ownerReviews.fuelEconomy')]: review.fuelEconomyRating,
                [t('seoSrp.ownerReviews.reliability')]: review.maintenanceRating,
              }}
              color="red"
            />
          </div>

          <button
            onClick={onViewLess}
            className="owner-review-toggler-btn"
            data-testid="owner-review-view-less-btn"
          >
            {t('seoSrp.ownerReviews.viewLess')}
          </button>
        </div>
      )}

      <style jsx>{`
        .owner-review {
          box-shadow: 0px 1px 0px #d3d3d3;
          margin-bottom: 29px;
          height: 340px;
        }
        .expanded-review {
          height: auto;
        }

        .owner-review-toggler-btn {
          color: ${colors.ALR1};
          background: none;
          border: none;
          padding: 0;
          margin-bottom: 16px;
        }

        .owner-review-toggler-btn:hover {
          text-decoration: underline;
        }

        .ratings {
          width: 250px;
          padding-bottom: 32px;
          padding-top: 8px;
        }
      `}</style>

      <style jsx global>{`
        .review-title-content.owner-review-title {
          padding-top: 12px;
          padding-bottom: 16px;
        }

        .review-title-content.section-title {
          padding-bottom: 8px;
          padding-top: 8px;
        }

        .review-title-content.score-title {
          padding-bottom: 16px;
        }
      `}</style>
    </div>
  );
}
